import moment from "moment";

import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { Guid } from "@/Common/Models/Guid";
import { Person } from "@/Common/Models/Person/Person";

class SearchPatientV2Result {
    public ContactId: Guid;
    public ContactFacetId: Guid;
    public FirstName: string;
    public LastName: string;
    public PreferredName: string;
    public DateOfBirth: string;// convert to date
    public NAM: string;
    public GenderLookupId: string;
    public SuffixLookupId: string;
    public PrefixLookupId: string;
    public MotherFirstName: string;
    public MotherLastName: string;
    public FatherFirstName: string;
    public FatherLastName: string;
    public PhoneNumbers: PatientPhoneNumber[];
    public HealthCards: PatientHealthCard[];
    public Addresses: PatientAddress[];
    public LastUpdatedDate: string;

    public constructor(item?: Partial<SearchPatientV2Result>) {
        if (item) {
            Object.assign(this, item);
            if (!isArrayNullOrUndefinedOrEmpty(item.PhoneNumbers))
                this.PhoneNumbers = item.PhoneNumbers.map(x => new PatientPhoneNumber(x));
            if (!isArrayNullOrUndefinedOrEmpty(item.Addresses))
                this.Addresses = item.Addresses.map(x => new PatientAddress(x));
            if (!isArrayNullOrUndefinedOrEmpty(item.HealthCards))
                this.HealthCards = item.HealthCards.map(x => new PatientHealthCard(x));
        }
    }

    public toDisplayableString(): string {
        let fullname = NameHelper.formatFullNameForHasName(this);
        if (!this.NAM)
            return fullname;

        return `${fullname} (${this.NAM})`;
    }

    public toPerson(): Person {
        let person = new Person();
        person.ContactId = this.ContactId;
        person.ContactFacetId = this.ContactFacetId;
        person.LastName = this.LastName;
        person.FirstName = this.FirstName;
        person.DateOfBirth = this.DateOfBirth ? moment(this.DateOfBirth).toDate() : null;
        person.NAM = this.NAM;
        person.motherFirstName = this.MotherFirstName;
        person.motherLastName = this.MotherLastName;
        person.prefix = this.PrefixLookupId ? cacheStore.GetLocalizedValueByLookupId(this.PrefixLookupId) : null;
        person.Phones = !isArrayNullOrUndefinedOrEmpty(this.PhoneNumbers) ? this.PhoneNumbers.map(x => x.toPhoneNumber()) : null;
        person.Addresses = !isArrayNullOrUndefinedOrEmpty(this.Addresses) ? this.Addresses.map(x => x.toAddress()) : null;
        return person;
    }
}

class PatientPhoneNumber {
    public Id: string;
    public ContactId: string;
    public Number: string;
    public Extension: string;
    public IsPreferred: boolean;
    public Note: string;
    public ContactTypeLookupId: string;
    public PhoneTypeLookupId: string;
    public CountryLookupId: string;
    public StateLookupId: string;

    public constructor(item?: Partial<PatientPhoneNumber>) {
        if (item)
            Object.assign(this, item);
    }

    public toDisplayableString(): string {
        return PhoneNumber.GetFormattedPhoneNumber(0, [this.toPhoneNumber()]);
    }

    public toPhoneNumber(): PhoneNumber {
        let phone = new PhoneNumber();
        phone.ContactPoint_id = this.Id;
        phone.Number = this.Number;
        phone.Extension = this.Extension;
        phone.Notes = this.Note;
        phone.IsPreferred = this.IsPreferred;
        phone.ContactPointTypeLookupId = this.ContactTypeLookupId;
        phone.CountryLookupId = this.CountryLookupId;
        phone.StateLookupId = this.StateLookupId;
        return phone;
    }
}

class PatientAddress {
    public Id: string;
    public ContactId: string;
    public Street: string;
    public City: string;
    public ZipCode: string;
    public ContactTypeLookupId: string;
    public CountryLookupId: string;
    public StateLookupId: string;

    public constructor(item?: Partial<PatientAddress>) {
        if (item)
            Object.assign(this, item);
    }

    public toDisplayableString(): string {
        return Address.GetFormattedAddress([this.toAddress()], 0, true);
    }

    public toAddress(): Address {
        let address = new Address();
        address.Id = this.Id;
        address.ContactPoint_id = this.Id;
        address.StreetAddress = this.Street;
        address.City = this.City;
        address.ZipCode = this.ZipCode;
        address.ContactPointTypeLookupId = this.ContactTypeLookupId;
        address.CountryLookupId = this.CountryLookupId;
        address.StateLookupId = this.StateLookupId;
        return address;
    }
}

class PatientHealthCard {
    public Id: string;
    public InactivableId: string;
    public Number: string;
    public Expiry: string;// convert to date
    public ProvinceLookupId: string;

    public constructor(item?: Partial<PatientHealthCard>) {
        if (item)
            Object.assign(this, item);
    }

    public toDisplayableString(): string {
        if (!this.Expiry)
            return this.Number;

        return `${this.Number} (${moment(this.Expiry).format("YY / MM")})`;
    }
}

export { PatientAddress, PatientPhoneNumber, SearchPatientV2Result };
